import { useState, useRef, useEffect, useCallback } from 'react';
import './SelectDropdown.css';

export interface SelectOption {
  value: string | number;
  label: string;
  /** Optional style to apply to the option (e.g., fontFamily for font preview) */
  style?: React.CSSProperties;
}

interface SelectDropdownProps {
  options: SelectOption[];
  value: string | number;
  onChange: (value: string | number) => void;
  /** Optional placeholder when no value is selected */
  placeholder?: string;
  /** Optional class name for custom styling */
  className?: string;
  /** If true, applies the selected option's style to the trigger */
  previewStyle?: boolean;
}

/**
 * Reusable select dropdown component with clean design
 * Features: keyboard navigation, click outside to close, scroll into view
 */
function SelectDropdown({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
  previewStyle = false
}: SelectDropdownProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);
  const selectedIndex = options.findIndex(opt => opt.value === value);

  /**
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  /**
   * Scroll highlighted option into view
   */
  useEffect(() => {
    if (isOpen && listRef.current && highlightedIndex >= 0) {
      const items = listRef.current.querySelectorAll('.select-dropdown-option');
      const item = items[highlightedIndex] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [isOpen, highlightedIndex]);

  /**
   * Reset highlighted index when opening
   */
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [isOpen, selectedIndex]);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => Math.min(prev + 1, options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          onChange(options[highlightedIndex].value);
          setIsOpen(false);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      case 'Home':
        e.preventDefault();
        setHighlightedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlightedIndex(options.length - 1);
        break;
    }
  }, [isOpen, highlightedIndex, options, onChange]);

  /**
   * Handle option click
   */
  const handleOptionClick = (optionValue: string | number) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  /**
   * Handle trigger click
   */
  const handleTriggerClick = () => {
    setIsOpen(prev => !prev);
  };

  // Get style for trigger if previewStyle is enabled
  const triggerStyle = previewStyle && selectedOption?.style ? selectedOption.style : undefined;

  return (
    <div
      ref={containerRef}
      className={`select-dropdown ${className} ${isOpen ? 'select-dropdown--open' : ''}`}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <button
        type="button"
        className="select-dropdown-trigger"
        onClick={handleTriggerClick}
        style={triggerStyle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="select-dropdown-trigger-text">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="select-dropdown-trigger-icon">
          <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
        </span>
      </button>

      {isOpen && (
        <div ref={listRef} className="select-dropdown-list" role="listbox">
          {options.map((option, index) => (
            <div
              key={option.value}
              className={`select-dropdown-option ${option.value === value ? 'select-dropdown-option--selected' : ''} ${index === highlightedIndex ? 'select-dropdown-option--highlighted' : ''}`}
              onClick={() => handleOptionClick(option.value)}
              onMouseEnter={() => setHighlightedIndex(index)}
              style={option.style}
              role="option"
              aria-selected={option.value === value}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SelectDropdown;
